import { useStore } from 'hooks/useStore';
import {
  BpmByNDCAndJobType,
  BpmByNDCAndJobTypePayload
} from 'Interfaces/ReferenceInterfaces/StandardAndRecipeBpmInterface';
import { useEffect, useRef, useState } from 'react';
import { DataTableStateEvent } from 'primereact/datatable';
import { useInterval } from 'hooks/usePolling';
import { getJitteredInterval } from 'utils/pollingUtility';
import { debounce } from 'lodash';
import * as XLSX from 'xlsx';
import {toast} from 'react-toastify';

interface FormData {
  roomTypeId: number;
  ndc: string;
  jobTypeId: number;
  description: string;
  bpm: number;
  moHc: number;
  paHc: number;
}

interface PaginationInfo {
  currentPage: number;
  totalPages: number;
  totalItems: number;
  hasNext: boolean;
  hasPrevious: boolean;
  first: number;
  rows: number;
}

export const useBpmByNdcAndJobTypeTable = (pageSize: number = 100) => {
  const { bpmByNdcAndJobTypeStore, roomStore, runTypeStore } = useStore();

  const [selectedRow, setSelectedRow] = useState<BpmByNDCAndJobType | null>(null);
  const [showAddModal, setShowAddModal] = useState(false);
  const [showDeleteModal, setShowDeleteModal] = useState(false);
  const [currentPage, setCurrentPage] = useState(1);
  const itemMenu = useRef<any>(null);
  const [searchQuery, setSearchQuery] = useState('');
  const environment = import.meta.env.VITE_ENVIRONMENT;
  // Polling interval in milliseconds (30 seconds)
  const POLLING_INTERVAL = getJitteredInterval();

  // Load initial data when component mounts
  useEffect(() => {
    bpmByNdcAndJobTypeStore.fetchBpmByNdcAndJobTypes(currentPage, pageSize);
    bpmByNdcAndJobTypeStore.fetchAllFgNDC();
    roomStore.fetchRooms();
    roomStore.fetchRoomTypes();
    runTypeStore.fetchRunTypes();
  }, []);

  // Set up polling for data updates
  useInterval(async () => {
    try {
      await bpmByNdcAndJobTypeStore.pollBpmByNdcAndJobTypes(
        currentPage,
        pageSize,
        searchQuery || undefined
      );
      roomStore.pollRoomTypes();
    } catch (error) {
      if(environment === 'development'){
        console.error('Error during polling:', error);
      }
      toast.error('Error during polling for BPM by NDC and Job Type');
    }
  }, POLLING_INTERVAL);

  // Handle page change
  const onPageChange = (event: DataTableStateEvent) => {
    if (event.page !== undefined) {
      const newPage = event.page + 1; // PrimeReact uses 0-based indexing, our API uses 1-based
      setCurrentPage(newPage);
      bpmByNdcAndJobTypeStore.fetchBpmByNdcAndJobTypes(newPage, pageSize, searchQuery || undefined);
    }
  };

  // Prepare room options from roomStore room types
  const roomOptions = roomStore.roomTypeOptions;

  // Prepare job type options from runTypeStore (all available job types)
  const jobTypeOptions = runTypeStore.runTypes.map(runType => ({
    label: runType.description || '',
    value: runType.id
  }));

  const handleAddRow = async (formData: FormData): Promise<void> => {
    if (selectedRow) {
      const updatePayload: BpmByNDCAndJobTypePayload = {
        room_type: formData.roomTypeId,
        ndc: formData.ndc,
        job_type: formData.jobTypeId,
        bpm: formData.bpm,
        mo_hc: formData.moHc,
        pa_hc: formData.paHc,
        description: formData.description
      };

      const success = await bpmByNdcAndJobTypeStore.updateBpmByNdcAndJobType(
        selectedRow.id,
        updatePayload
      );
      if (!success) {
        throw new Error('Failed to update BPM by NDC and job type');
      }
    } else {
      const createPayload: BpmByNDCAndJobTypePayload = {
        room_type: formData.roomTypeId,
        ndc: formData.ndc,
        job_type: formData.jobTypeId,
        bpm: formData.bpm,
        mo_hc: formData.moHc,
        pa_hc: formData.paHc,
        description: formData.description,
        flag: true
      };

      const success = await bpmByNdcAndJobTypeStore.createBpmByNdcAndJobType(createPayload);
      if (!success) {
        throw new Error('Failed to create BPM by NDC and job type');
      }
    }
    setSelectedRow(null);
    await bpmByNdcAndJobTypeStore.fetchBpmByNdcAndJobTypes(
      currentPage,
      pageSize,
      searchQuery || undefined
    );
  };

  const handleDeleteRow = async () => {
    if (selectedRow) {
      await bpmByNdcAndJobTypeStore.deleteBpmByNdcAndJobType(selectedRow.id);

      // After delete, refetch the current page
      const totalPages = bpmByNdcAndJobTypeStore.pagination.totalPages;
      const newPage = currentPage > totalPages ? Math.max(totalPages, 1) : currentPage;

      if (newPage !== currentPage) {
        setCurrentPage(newPage);
      }

      bpmByNdcAndJobTypeStore.fetchBpmByNdcAndJobTypes(newPage, pageSize, searchQuery || undefined);
      setSelectedRow(null);
      setShowDeleteModal(false);
    }
  };

  const prepareFormData = (row: BpmByNDCAndJobType): FormData => {
    return {
      roomTypeId: row.room_type.id,
      ndc: row.ndc,
      jobTypeId: row.job_type.id,
      description: row.description,
      bpm: row.bpm,
      moHc: row.mo_hc,
      paHc: row.pa_hc
    };
  };

  // Debounced search function
  const debouncedSearch = debounce((query: string) => {
    bpmByNdcAndJobTypeStore.fetchBpmByNdcAndJobTypes(1, pageSize, query || undefined);
    setCurrentPage(1); // Reset to first page on new search
  }, 500);

  const handleSearchChange = (query: string) => {
    setSearchQuery(query);
    debouncedSearch(query);
  };

  // Method to manually refresh data
  const refreshData = async () => {
    await bpmByNdcAndJobTypeStore.refreshData(currentPage, pageSize, searchQuery || undefined);
  };

  const handleExportToExcel = () => {
    const filteredData = bpmByNdcAndJobTypeStore.bpmByNdcAndJobTypes.filter(item => {
      if (!searchQuery.trim()) {
        return true;
      }
      const searchLower = searchQuery.toLowerCase();
      return (
        item.ndc.toLowerCase().includes(searchLower) ||
        item.description.toLowerCase().includes(searchLower) ||
        item.job_type.description.toLowerCase().includes(searchLower) ||
        item.room_type.description.toLowerCase().includes(searchLower)
      );
    });

    const exportData = filteredData.map(item => ({
      'NDC': item.ndc,
      'Description': item.description,
      'Job Type': item.job_type.description,
      'Room Type': item.room_type.description,
      'BPM': item.bpm,
      'MO HC': item.mo_hc,
      'PA HC': item.pa_hc
    }));

    const worksheet = XLSX.utils.json_to_sheet(exportData);
    const workbook = XLSX.utils.book_new();
    XLSX.utils.book_append_sheet(workbook, worksheet, 'BPM by NDC and Job Type');
    XLSX.writeFile(workbook, 'bpm_by_ndc_and_job_type_export.xlsx');
  };

  // Create pagination info with the required fields
  const pagination: PaginationInfo = {
    ...bpmByNdcAndJobTypeStore.pagination,
    first: (currentPage - 1) * pageSize,
    rows: pageSize
  };

  return {
    bpmByNdcAndJobTypeData: bpmByNdcAndJobTypeStore.bpmByNdcAndJobTypes,
    isLoading:
      bpmByNdcAndJobTypeStore.isLoading || roomStore.isLoading || roomStore.roomTypesLoading,
    pagination,
    itemMenu,
    selectedRow,
    showAddModal,
    showDeleteModal,
    setSelectedRow,
    setShowAddModal,
    setShowDeleteModal,
    handleAddRow,
    handleDeleteRow,
    prepareFormData,
    roomOptions,
    onPageChange,
    currentPage,
    handleSearchChange,
    searchQuery,
    jobTypeOptions,
    refreshData,
    handleExportToExcel,
    fgNDC: bpmByNdcAndJobTypeStore.fgNDC
  };
};
